import React, { useEffect, useState } from "react";
import { Plus, Trophy, Shuffle, Lightbulb, Swords, Clock, CheckCircle2, XCircle } from "lucide-react";
import axios from "axios";
import { generateApiKey } from "../../../../helpers/common.helpers";
import Swal from "sweetalert2";
import JobOpertunites from "./JobOpertunites";
import { FileText, Github, Book } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Custom Card Components to replace Shadcn Card
const Card = ({ children, className = "" }) => {
  return (
    <div
      id="cod"
      className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const CardContent = ({ children, className = "" }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>;
};

const CardHeader = ({ children, className = "" }) => {
  return (
    <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>
      {children}
    </div>
  );
};

const CardTitle = ({ children, className = "" }) => {
  return (
    <h3
      className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
    >
      {children}
    </h3>
  );
};

// Custom Button Component to replace Shadcn Button
const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

  const variantClasses = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline:
      "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  };

  const sizeClasses = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3",
    lg: "h-11 rounded-md px-8",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Custom Label Component to replace Shadcn Label
const Label = ({ children, className = "", ...props }) => {
  return (
    <label
      className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}
      {...props}
    >
      {children}
    </label>
  );
};

// Custom Badge Component to replace Shadcn Badge
const Badge = ({ children, className = "", variant = "default" }) => {
  const variantClasses = {
    default: "bg-primary text-primary-foreground hover:bg-primary/80",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    destructive:
      "bg-destructive text-destructive-foreground hover:bg-destructive/80",
    outline: "text-foreground",
  };

  return (
    <div
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variantClasses[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

const DailyChallengeCard = () => {
  // Challenge of the Day API States
  const [challengeData, setChallengeData] = useState(null);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [coinData, setCoinData] = useState(null);
  const [isFirstAttempt, setIsFirstAttempt] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");

  const navigate = useNavigate();

  // New states for shuffle and hint functionality
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [shuffleCount, setShuffleCount] = useState(0);

  // Loader functions
  const LoaderOpen = () => {
    setChallengeLoading(true);
  };

  const LoaderClose = () => {
    setChallengeLoading(false);
  };

  // Calculate time remaining until midnight
  useEffect(() => {
    if (challengeData?.status === "completed") {
      const calculateTimeRemaining = () => {
        const now = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);

        const diff = tomorrow - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        return `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      };

      setTimeRemaining(calculateTimeRemaining());
      const interval = setInterval(() => {
        setTimeRemaining(calculateTimeRemaining());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [challengeData]);

  // Shuffle API call function
  const shuffleFetchData = async () => {
    if (isSubmitted || (challengeData && challengeData.attempt > 0)) {
      return;
    }

    if (shuffleCount >= 3) {
      Swal.fire({
        title: "Shuffle Limit Reached",
        text: "You can only shuffle 3 times per question.",
        icon: "info",
        confirmButtonText: "OK",
      });
      return;
    }

    LoaderOpen();
    try {
      const response = await axios({
        method: "post",
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
        url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/dashboardhomepage/codshuffle`,
        data: {
          enrol_id: localStorage.getItem("enrol_id"),
          category: localStorage.getItem("category"),
          scount: shuffleCount + 1,
        },
      });

      if (response.data && response.data[0]) {
        const updatedData = response.data[0];
        setChallengeData(updatedData);
        localStorage.setItem("codData", JSON.stringify(updatedData));
        setShuffleCount(updatedData.shuffle || shuffleCount + 1);

        setShuffledOptions([]);
        setSelectedAnswer("");

        LoaderClose();
      }
    } catch (error) {
      console.error("Error shuffling options:", error);
      LoaderClose();
      Swal.fire({
        title: "Error",
        text: "Failed to shuffle options. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleCompete = () => {
    navigate("/Competition-corner");
  };

  // Fetch coin data function
  const fetchCoinData = async () => {
    try {
      const enrol_id = localStorage.getItem("enrol_id");

      const options = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/fetchstudentcoins`,
        JSON.stringify({ enrol_id }),
        options
      );

      if (response.data) {
        const coins = response.data;
        setCoinData(coins);
        localStorage.setItem("coins", coins.toString());
      }
    } catch (error) {
      console.error("Error fetching coin data:", error);
    }
  };

  // API Integration - Fetch Challenge of the Day
  const fetchChallengeData = async () => {
    try {
      setChallengeLoading(true);

      const options = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/dashboardhomepage/fetchcod`,
        JSON.stringify({
          enrol_id: Number(localStorage.getItem("enrol_id")),
          category: Number(localStorage.getItem("category")),
        }),
        options
      );

      if (response.data.status === 404) {
        setChallengeData(null);
        Swal.fire({
          title: "Info",
          text: "You've reached the end of the available questions. More to come soon!",
          icon: "info",
          confirmButtonText: "OK",
        });
      } else {
        const newChallenge = response.data[0];
        setChallengeData(newChallenge);
        localStorage.setItem("codData", JSON.stringify(newChallenge));
        setShuffleCount(newChallenge.shuffle || 0);

        // Check if challenge is already completed
        if (newChallenge.status === "completed") {
          setIsSubmitted(true);
          setShowAnswer(true);
          setSelectedAnswer(newChallenge.user_answer?.toString() || "");
        } else if (newChallenge.attempt > 0) {
          setIsSubmitted(true);
          setShowAnswer(true);
          setSelectedAnswer(newChallenge.user_answer?.toString() || "");
        } else {
          setIsSubmitted(false);
          setShowAnswer(false);
          setSelectedAnswer("");
          setIsFirstAttempt(true);
        }
      }
      setChallengeLoading(false);
    } catch (error) {
      console.error("Error fetching challenge data:", error);
      setChallengeLoading(false);
    }
  };

  // API Integration - Submit Challenge Answer
  const submitChallengeAnswer = async () => {
    if (!selectedAnswer) {
      Swal.fire({
        title: "Warning",
        text: "Please select at least one option",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      setChallengeLoading(true);

      // Calculate points based on difficulty
      let points = 0;
      if (
        challengeData.answer === parseInt(selectedAnswer) &&
        challengeData.attempt === 0
      ) {
        if (challengeData.difficulty === "Easy") points = 5;
        else if (challengeData.difficulty === "Medium") points = 10;
        else if (challengeData.difficulty === "Hard") points = 15;
      }

      if (challengeData.attempt == 0) {
        setIsFirstAttempt(true);
      } else {
        setIsFirstAttempt(false);
      }

      const submitData = {
        question_id: challengeData.qid,
        cod_option: parseInt(selectedAnswer),
        difficulty: challengeData.difficulty,
        attempt: challengeData.attempt == 0 ? 0 : challengeData.attempt + 1,
        answer: challengeData.answer,
        enrol_id: localStorage.getItem("enrol_id"),
        coins: coinData ? coinData : localStorage.getItem("coins"),
        point: points,
      };

      const options = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/dashboardhomepage/submitdata`,
        JSON.stringify(submitData),
        options
      );

      // Update challenge data with new attempt count and user answer
      const updatedChallengeData = {
        ...challengeData,
        attempt: challengeData.attempt + 1,
        user_answer: parseInt(selectedAnswer),
        status: "completed",
      };
      setChallengeData(updatedChallengeData);
      localStorage.setItem("codData", JSON.stringify(updatedChallengeData));

      // Update coins
      if (challengeData.attempt === 0) {
        const newCoins =
          (coinData ? coinData : parseInt(localStorage.getItem("coins"))) +
          points;
        localStorage.setItem("coins", newCoins.toString());
        setCoinData(newCoins);
      }

      setIsSubmitted(true);
      setShowAnswer(true);
      setChallengeLoading(false);
    } catch (error) {
      console.error("Error submitting challenge answer:", error);
      setChallengeLoading(false);
    }
  };

  // Handle option click
  const handleOptionClick = (optionValue) => {
    if (!isSubmitted) {
      setSelectedAnswer(optionValue);
    }
  };

  // Shuffle handler
  const handleShuffle = () => {
    shuffleFetchData();
  };

  // Handle hint toggle
  const handleHintToggle = () => {
    setShowHint(!showHint);
  };

  // Get current date for badge
  const getCurrentDate = () => {
    const date = new Date();
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  };

  // Handle reattempt
  const handleReattempt = () => {
    setIsSubmitted(false);
    setShowAnswer(false);
    setSelectedAnswer("");
    setShowHint(false);
    setShuffledOptions([]);
    setShuffleCount(0);
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchChallengeData();
    fetchCoinData();
  }, []);

  // Initialize shuffled options when challenge data changes
  useEffect(() => {
    if (displayChallenge) {
      const options = [
        { value: "1", text: displayChallenge.option1 },
        { value: "2", text: displayChallenge.option2 },
        { value: "3", text: displayChallenge.option3 },
        { value: "4", text: displayChallenge.option4 },
      ];
      setShuffledOptions(options);
    }
  }, [challengeData]);

  // Default challenge data if API fails
  const defaultChallenge = {
    question:
      "Compared with users who skip a mobile-app walkthrough tutorial, users who sit through the tutorial will:",
    option1: "Be faster to perform a task within the app",
    option2: "Be less likely to successfully complete a task within the app",
    option3: "Perceive a task within the app as more difficult",
    option4: "Enjoy the app more",
    answer: 1,
    difficulty: "Easy",
    attempt: 0,
  };

  const displayChallenge = challengeData || defaultChallenge;

  // Get options to display (shuffled or original)
  const optionsToDisplay =
    shuffledOptions.length > 0
      ? shuffledOptions
      : [
        { value: "1", text: displayChallenge.option1 },
        { value: "2", text: displayChallenge.option2 },
        { value: "3", text: displayChallenge.option3 },
        { value: "4", text: displayChallenge.option4 },
      ];

  // Check if user's answer is correct
  const isCorrectAnswer =
    challengeData && selectedAnswer
      ? challengeData.answer === parseInt(selectedAnswer)
      : false;

  return (
    <Card className="lg:col-span-2 h-[38rem] flex flex-col backdrop-blur-sm bg-white/10 border border-border pb-2 shadow-sm">
      <CardHeader className="flex-shrink-0 pb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-500/20 backdrop-blur-sm border border-violet-500/30 rounded-xl flex items-center justify-center">
              <Trophy className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold bg-clip-text">
                Challenge of the Day
              </CardTitle>
            </div>
          </div>
          <Button
            onClick={handleCompete}
            className="hidden md:flex border-2 border-violet-400 text-violet-500 font-medium px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 items-center gap-2"
          >
            <Swords className="w-4 h-4" />
            Compete Now
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 flex-1 overflow-y-auto custom-scrollbar">
        {challengeLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
          </div>
        ) : (
          <>
            {/* Small Challenge Completed Banner - Only if status is completed */}
            {challengeData?.status === "completed" && (
              <div className={`p-3 rounded-lg border ${isCorrectAnswer ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {isCorrectAnswer ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  )}
                  <p className={`text-xs font-medium ${isCorrectAnswer ? 'text-green-800' : 'text-red-800'}`}>
                    Challenge Completed! {isCorrectAnswer ? "You got it right! 🎉" : "Keep learning!"}
                  </p>
                </div>
              </div>
            )}

            {/* Question */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
              <div className="flex items-start justify-between gap-4">
                <h4 className="text-sm font-medium text-gray-800 leading-relaxed">
                  Q. {displayChallenge.question}
                </h4>
                {challengeData && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {challengeData.difficulty}
                    </span>
                    {challengeData.attempt === 0 && !challengeData.status && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        +
                        {challengeData.difficulty === "Easy"
                          ? "5"
                          : challengeData.difficulty === "Medium"
                            ? "10"
                            : "15"}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Hint */}
            {showHint && challengeData && challengeData.hint_text && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800">
                      {challengeData.hint_text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Options - Color coded when completed */}
            <div className="space-y-3">
              {optionsToDisplay.map((option) => {
                const isCorrectOption = challengeData && challengeData.answer === parseInt(option.value);
                const isUserSelectedOption = selectedAnswer === option.value;
                const showAsCorrect = challengeData?.status === "completed" && isCorrectOption;
                const showAsWrong = challengeData?.status === "completed" && isUserSelectedOption && !isCorrectOption;

                return (
                  <div key={option.value} className="group">
                    <div
                      className={`flex items-center space-x-3 p-4 rounded-xl border transition-all duration-200 ${challengeData?.status === "completed"
                        ? showAsCorrect
                          ? "bg-green-50 border-green-300 shadow-md cursor-default"
                          : showAsWrong
                            ? "bg-red-50 border-red-300 shadow-md cursor-default"
                            : "bg-white/50 border-border cursor-default"
                        : isUserSelectedOption
                          ? "bg-blue-50 border-blue-300 shadow-md cursor-pointer"
                          : "bg-white/50 border-border hover:border-blue-300 hover:shadow-md cursor-pointer"
                        } ${isSubmitted && challengeData?.status !== "completed" ? "pointer-events-none" : ""}`}
                      onClick={() => !challengeData?.status && handleOptionClick(option.value)}
                    >
                      {/* Radio Button or Check/Cross Icon */}
                      {challengeData?.status === "completed" ? (
                        showAsCorrect ? (
                          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        ) : showAsWrong ? (
                          <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs">✗</span>
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-white"></div>
                        )
                      ) : (
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isUserSelectedOption
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300 bg-white"
                            }`}
                        >
                          {isUserSelectedOption && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                      )}

                      <Label className={`text-sm cursor-pointer flex-1 ${showAsCorrect ? 'text-green-800 font-medium' :
                        showAsWrong ? 'text-red-800 font-medium' :
                          'text-muted-foreground'
                        }`}>
                        {option.text}
                      </Label>

                      {/* Points badge for correct first attempt */}
                      {showAsCorrect && isFirstAttempt && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                          +
                          {challengeData.difficulty === "Easy"
                            ? "5"
                            : challengeData.difficulty === "Medium"
                              ? "10"
                              : "15"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Small Countdown Timer - Only when completed */}
            {challengeData?.status === "completed" && (
              <div className="p-3 rounded-lg bg-violet-50 border border-violet-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-violet-600" />
                    <p className="text-xs font-medium text-violet-800">
                      Next Challenge Available In
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="flex flex-col items-center bg-white px-2 py-1 rounded border border-violet-200">
                      <span className="text-base font-bold text-violet-600 font-mono leading-none">
                        {timeRemaining.split(':')[0]}
                      </span>
                      <span className="text-[8px] text-violet-400 font-medium mt-0.5">HR</span>
                    </div>
                    <span className="text-violet-400 font-bold">:</span>
                    <div className="flex flex-col items-center bg-white px-2 py-1 rounded border border-violet-200">
                      <span className="text-base font-bold text-violet-600 font-mono leading-none">
                        {timeRemaining.split(':')[1]}
                      </span>
                      <span className="text-[8px] text-violet-400 font-medium mt-0.5">MIN</span>
                    </div>
                    <span className="text-violet-400 font-bold">:</span>
                    <div className="flex flex-col items-center bg-white px-2 py-1 rounded border border-violet-200">
                      <span className="text-base font-bold text-violet-600 font-mono leading-none">
                        {timeRemaining.split(':')[2]}
                      </span>
                      <span className="text-[8px] text-violet-400 font-medium mt-0.5">SEC</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Shuffle and Hint buttons - Only when not completed */}
            {!challengeData?.status && (
              <div className="flex gap-3">
                <Button
                  onClick={handleShuffle}
                  disabled={
                    isSubmitted ||
                    (challengeData && challengeData.attempt > 0) ||
                    shuffleCount >= 3
                  }
                  variant="outline"
                  className="flex-1 h-10 bg-white/80 hover:bg-white border border-gray-200 hover:border-blue-300 text-gray-700 font-medium rounded-xl transition-all duration-300 disabled:opacity-50"
                >
                  <Shuffle className="w-4 h-4 mr-2" />
                  Shuffle
                </Button>

                {challengeData && challengeData.hint_text && (
                  <Button
                    onClick={handleHintToggle}
                    variant="outline"
                    className="flex-1 h-10 bg-white/80 hover:bg-white border border-gray-200 hover:border-amber-300 text-gray-700 font-medium rounded-xl transition-all duration-300"
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    {showHint ? "Hide Hint" : "Show Hint"}
                  </Button>
                )}
              </div>
            )}

            <Button
              onClick={handleCompete}
              className="md:hidden w-full h-10 border-2 border-violet-400 text-violet-500 font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Swords className="w-4 h-4" />
              Compete Now
            </Button>

            {/* Submit/Retry Button */}
            {!challengeData?.status && (
              <div className="mt-auto pt-0 md:pt-4">
                {!isSubmitted ? (
                  <Button
                    onClick={submitChallengeAnswer}
                    disabled={challengeLoading || !selectedAnswer}
                    className="w-full h-10 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {challengeLoading ? "Submitting..." : "Complete Challenge"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleReattempt}
                    className="w-full h-10 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Retry Challenge
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
    </Card>
  );
};

export default DailyChallengeCard;
